import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactDropdown from 'shared/design-system/components/TrueImpactDropdown';
import { ReportAPIResponse } from '../api';
import {
  FeedbackStatus,
  ReportStage,
  ReportStages,
  ReportStatuses,
} from 'donor/portal/pages/FeedbackDashboard/api';
import { ReportingType } from '../NewReport/api';
import { REPORTING_TYPE_LABEL } from '../../ProgramsAndReports/constants';
import { faSignalStream } from '@fortawesome/pro-light-svg-icons';
import { TrueImpactButtonWithLoading } from 'shared/design-system/components/TrueImpactButton';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { useClient } from 'shared/apollo';
import { submitAdminReport } from '../../ReportBuilder/Components/Review/api';
import { pullReportData, useTrackEvent } from 'shared/utils/useTrackEvent';
import { useReport } from 'shared/providers/ReportProvider';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import { Selector } from 'shared/design-system/components/Selector';

export interface AdminModalContentProps {
  report:
    | ReportAPIResponse
    | {
        dbId: ReportAPIResponse['dbId'];
        stage: ReportAPIResponse['stage'];
        status: ReportAPIResponse['status'];
        year: ReportAPIResponse['year'];
        reportingType: ReportAPIResponse['reportingType'];
        feedbackType: ReportAPIResponse['feedbackType'];
        optedInFullReporting: ReportAPIResponse['optedInFullReporting'];
      };
}

const REPORTING_TYPE_OPTIONS: ReportingType[] = ['FULL', 'LIGHT'];

const StyledDiv = styled.div`
  text-align: center;
  padding: ${SPACERS.medium};
  .feedback-type {
    margin-bottom: ${SPACERS.tiny};
  }
  .text-message,
  .dropdown-field {
    margin-bottom: ${SPACERS.small};
  }
  .checkbox-field {
    margin-bottom: ${SPACERS.small};
    max-width: 100%;
  }
  .status-save-button {
    margin-top: ${SPACERS.small};
    margin-bottom: ${SPACERS.tiny};
    button {
      min-width: 220px;
    }
  }
`;

const AdminModalContent: React.FC<AdminModalContentProps> = ({ report }) => {
  const client = useClient();
  const trackEvent = useTrackEvent();
  const { userStaticType } = useUserActions();
  const { report: reportState, isReportEndDatePassed } = useReport();
  const [
    requestUpdateReportState,
    makeUpdateReportRequest,
  ] = useGraphQLRequest();
  const [stage, setStage] = useState<ReportStage>(report.stage);
  const [status, setStatus] = useState<FeedbackStatus>(report.status);
  const [reportingType, setReportingType] = useState<ReportingType>(
    (report.reportingType as ReportingType) || 'FULL',
  );
  const [doNotSendNotifications, setDoNotSendNotifications] = useState<boolean>(
    false,
  );
  const [optedInFullReporting, setOptedInFullReporting] = useState<boolean>(
    Boolean(report.optedInFullReporting),
  );
  const handleSubmit = useCallback(() => {
    if (!client) return;
    const request = submitAdminReport(
      client,
      report.dbId,
      status,
      stage,
      !doNotSendNotifications,
      reportingType,
      optedInFullReporting,
    );
    request.then(() => {
      const reportData =
        reportState.type === 'REQUEST_SUCCESS' ? reportState.data : undefined;
      trackEvent('ReportStatusChanged', {
        ...(reportData
          ? pullReportData(reportData, isReportEndDatePassed, userStaticType)
          : {}),
        previousStatus: report.status,
        previousStage: report.stage,
        status,
        stage,
        isAdminModal: true,
      });
    });
    makeUpdateReportRequest(request);
  }, [
    client,
    status,
    stage,
    reportingType,
    report,
    doNotSendNotifications,
    optedInFullReporting,
    makeUpdateReportRequest,
  ]);

  return (
    <StyledDiv>
      <TrueImpactText className="feedback-type">
        Feedback Cycle: <strong>{report.feedbackType}</strong>
      </TrueImpactText>
      <TrueImpactDropdown
        onClear={undefined}
        value={stage}
        icon={faSignalStream}
        getId={x => x}
        getLabel={x => x}
        native={true}
        getItemDisability={undefined}
        name="-- Select Report Stage --"
        onChange={setStage}
        options={ReportStages}
        className="dropdown-field"
      />
      <TrueImpactDropdown
        onClear={undefined}
        value={status}
        icon={faSignalStream}
        getId={x => x}
        getLabel={x => x}
        native={true}
        getItemDisability={undefined}
        name="-- Select Report Statuses --"
        onChange={setStatus}
        options={ReportStatuses}
        className="dropdown-field"
      />
      <TrueImpactDropdown
        onClear={undefined}
        value={reportingType}
        icon={faSignalStream}
        getId={x => x}
        getLabel={x => REPORTING_TYPE_LABEL[x] || x}
        native={true}
        getItemDisability={undefined}
        name="-- Select Reporting Type --"
        onChange={setReportingType}
        options={REPORTING_TYPE_OPTIONS}
        className="dropdown-field"
      />
      <Selector
        variant="checkbox"
        label="Do not send notifications"
        selected={doNotSendNotifications}
        onClick={() => setDoNotSendNotifications(!doNotSendNotifications)}
        className="checkbox-field"
      />
      <Selector
        variant="checkbox"
        label="Opted in full reporting"
        selected={optedInFullReporting}
        onClick={() => setOptedInFullReporting(!optedInFullReporting)}
        className="checkbox-field"
      />
      {requestUpdateReportState.type === 'REQUEST_ERROR' && (
        <TrueImpactText
          variant="body-small"
          color="critical"
          className="text-message"
        >
          {requestUpdateReportState.error.message || 'Unable to save.'}
        </TrueImpactText>
      )}
      {requestUpdateReportState.type === 'REQUEST_SUCCESS' && (
        <TrueImpactText
          variant="body-small"
          color="success"
          className="text-message"
        >
          Successfully updated the status. Please refresh the page to see the
          changes.
        </TrueImpactText>
      )}
      <TrueImpactButtonWithLoading
        className="status-save-button"
        disabled={
          (!status || status === report.status) &&
          (!stage || stage === report.stage) &&
          (!reportingType || reportingType === report.reportingType) &&
          optedInFullReporting === Boolean(report.optedInFullReporting)
        }
        onClick={handleSubmit}
        isLoading={requestUpdateReportState.type === 'REQUEST_START'}
      >
        Save
      </TrueImpactButtonWithLoading>
    </StyledDiv>
  );
};

export default AdminModalContent;
